import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import type {
  ClarificationAnswerItem,
  ClarificationOption,
  ClarificationQuestionItem,
  ClarificationRequest,
} from "@/features/chat/model/chatTypes";
import { normalizeClarificationRequest } from "@/features/chat/model/chatTypes";

type ClarificationQuestionBlockProps = {
  question: ClarificationQuestionItem;
  selectedOptionId: string | null;
  otherText: string;
  disabled?: boolean;
  onSelectOption: (optionId: string) => void;
  onOtherTextChange: (value: string) => void;
};

function ClarificationQuestionBlock({
  question,
  selectedOptionId,
  otherText,
  disabled = false,
  onSelectOption,
  onOtherTextChange,
}: ClarificationQuestionBlockProps) {
  const selectedIsOther = selectedOptionId === "other";

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        {question.question}
      </Typography>
      {question.reason ? (
        <Typography variant="body2" color="text.secondary">
          {question.reason}
        </Typography>
      ) : null}
      <Stack spacing={1}>
        {(question.options ?? []).map((option: ClarificationOption) => (
          <Card
            key={option.id}
            variant="outlined"
            sx={{
              borderColor: selectedOptionId === option.id ? "primary.main" : "divider",
            }}
          >
            <CardActionArea
              disabled={disabled}
              onClick={() => onSelectOption(option.id)}
              data-testid={`clarification-option-${question.id}-${option.id}`}
            >
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="body2" fontWeight={600}>
                  {option.label}
                </Typography>
                {option.description ? (
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                ) : null}
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
      {selectedIsOther ? (
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Please specify"
          value={otherText}
          disabled={disabled}
          onChange={(event) => onOtherTextChange(event.target.value)}
        />
      ) : null}
    </Stack>
  );
}

export type ClarificationPanelProps = {
  request: ClarificationRequest;
  disabled?: boolean;
  onSubmit: (answers: ClarificationAnswerItem[]) => void;
};

export function ClarificationPanel({
  request,
  disabled = false,
  onSubmit,
}: ClarificationPanelProps) {
  const normalized = useMemo(() => normalizeClarificationRequest(request), [request]);
  const questions = normalized?.questions ?? [];

  const [selections, setSelections] = useState<Record<string, string | null>>({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});

  const allAnswered = questions.every((question) => {
    const selectedOptionId = selections[question.id] ?? null;
    if (selectedOptionId === null) {
      return false;
    }
    if (selectedOptionId === "other") {
      return (otherTexts[question.id] ?? "").trim().length > 0;
    }
    return true;
  });

  const canSubmit = allAnswered && !disabled;

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    const answers: ClarificationAnswerItem[] = questions.map((question) => {
      const selectedOptionId = selections[question.id] ?? "";
      const isOther = selectedOptionId === "other";
      return {
        question_id: question.id,
        selected_option_id: selectedOptionId,
        custom_text: isOther ? (otherTexts[question.id] ?? "").trim() : null,
      };
    });
    onSubmit(answers);
  };

  if (questions.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack spacing={2}>
        {questions.length > 1 ? (
          <Stack spacing={0.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              I need a few clarifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Answer all questions to continue
            </Typography>
          </Stack>
        ) : null}
        {questions.map((question, index) => (
          <Stack key={question.id} spacing={2}>
            {index > 0 ? <Divider /> : null}
            <ClarificationQuestionBlock
              question={question}
              selectedOptionId={selections[question.id] ?? null}
              otherText={otherTexts[question.id] ?? ""}
              disabled={disabled}
              onSelectOption={(optionId) =>
                setSelections((current) => ({ ...current, [question.id]: optionId }))
              }
              onOtherTextChange={(value) =>
                setOtherTexts((current) => ({ ...current, [question.id]: value }))
              }
            />
          </Stack>
        ))}
        <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
          Continue
        </Button>
      </Stack>
    </Box>
  );
}

/** @deprecated Use ClarificationPanel */
export const ClarificationOptionsPanel = ClarificationPanel;
